'use client'

import { getOnlineItems, getStoreItems } from '@/feature/product/utils/productAvailabilityItem'
import { useTranslation } from '@/lib/i18n/useTranslation.client'
import { Icon } from '@/ui/icons/icon/Icon'
import { StatusLine } from '@/ui/status-line/StatusLine'
import type { ProductRow_ProductFragment } from '@gql-generated/graphql'
import { Availability, type Maybe, ShippingDelay } from '@gql-generated/graphql'
import { useMemo } from 'react'
import { ProductAvailability } from '../../product-availabilities/ProductAvailability.client'
import { ProductStatusLine } from '../../product-availabilities/ProductStatusLine.client'

type ProductRowStocksProps = {
  displayStoreQuantity: boolean
  filteredItemDeliveryDetails:
    | {
        availability: ShippingDelay
        date?: Maybe<string>
        realDeliveryDate?: Maybe<boolean>
        quantity: number
      }[]
    | undefined
  isClickAndCollectEnabled: boolean
  product: ProductRow_ProductFragment
  selectedQuantity?: number
  showAvailabilitiesCustomData?: boolean
}

/**
 *
 * This component only deals with the display of stocks for wishlists and cart items alike.
 * wishlists : displays online AND store stocks, expressed as "delivery within xx hours"
 * checkout : displays online stocks ONLY, expressed as "**estimated** delivery within xx hours"
 *
 */
export function ProductRowStocks({
  displayStoreQuantity,
  filteredItemDeliveryDetails = [],
  isClickAndCollectEnabled,
  product,
  selectedQuantity = 0,
  showAvailabilitiesCustomData = true,
}: ProductRowStocksProps) {
  const { Trans } = useTranslation(['productRow', 'productAvailabilities'])

  const storeItems = getStoreItems({
    product,
    shortMode: false,
    isClickAndCollectEnabled: Boolean(isClickAndCollectEnabled),
    showAvailabilitiesCustomData,
  })

  const onlineItems = getOnlineItems({
    product,
    selectedQuantity,
    isClarityDelayEnabled: true,
  })

  const displayLocalStock = useMemo(
    () =>
      Boolean(isClickAndCollectEnabled) &&
      product.stock.availability?.includes(Availability.Store) &&
      !product.stock.availability?.includes(Availability.StoppedInstore),
    [isClickAndCollectEnabled, product.stock.availability],
  )

  const isOnlineVisible = useMemo(
    () =>
      (!!product.stock.shippingDelay &&
        !product.stock.availability?.includes(Availability.StoppedOnline)) ||
      !displayLocalStock,
    [product.stock.shippingDelay, product.stock.availability, displayLocalStock],
  )

  return (
    <>
      {!displayStoreQuantity &&
        filteredItemDeliveryDetails.length > 0 &&
        filteredItemDeliveryDetails.map(({ availability, date, realDeliveryDate, quantity }) => {
          return (
            <StatusLine
              key={availability}
              variant={availability === ShippingDelay.Weeks_4 ? 'warning' : 'success'}
              data-testid={`estimated-shipping-delay/${availability}`}
            >
              <span>
                <Trans
                  i18nKey={
                    realDeliveryDate
                      ? 'productRow:deliveryDelay.realDate'
                      : `productRow:deliveryDelay.${availability}`
                  }
                  components={{
                    medium: <span className="font-medium" />,
                    bold: <span className="font-bold" />,
                  }}
                  values={{
                    date,
                    quantity,
                  }}
                />
              </span>
            </StatusLine>
          )
        })}

      {displayStoreQuantity && (
        <>
          {isOnlineVisible && (
            <div className="flex items-center gap-0.5">
              <ProductAvailability
                title={
                  <Trans
                    i18nKey="productAvailabilities:online.title"
                    components={{
                      uppercase: <span className="uppercase" />,
                    }}
                  />
                }
                TitleIcon={<Icon.Truck size="sm" />}
              >
                <div className="flex flex-col">
                  {onlineItems.map(({ id, props }) => (
                    <ProductStatusLine
                      key={id}
                      {...props}
                      shortMode={id === 'online-max-none'} // Product in restock phase
                    />
                  ))}
                </div>
              </ProductAvailability>
            </div>
          )}
          {displayLocalStock && (
            <div className="flex items-center gap-0.5">
              <ProductAvailability
                title={
                  <Trans
                    i18nKey="productAvailabilities:store.title"
                    components={{
                      uppercase: <span className="uppercase" />,
                      pointOfSale: <span />,
                    }}
                  />
                }
                TitleIcon={<Icon.Store size="sm" />}
                isTitleVisible={false}
              >
                {storeItems.map(({ id, props }) => (
                  <ProductStatusLine key={id} {...props} />
                ))}
              </ProductAvailability>
            </div>
          )}
        </>
      )}
    </>
  )
}
